import {} from "@apollo/client"
import { withTranslate } from "@pathwright/ui/src/components/"
import Button from "@pathwright/ui/src/components/button/Button"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import Text from "@pathwright/ui/src/components/ui/Text"
import {
  GroupFragmentDoc,
  ResourceFragmentDoc
} from "@pathwright/web/src/modules/api/generated"
import CohortIsland from "@pathwright/web/src/modules/cohort/CohortIsland"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import { getGroupUrl } from "@pathwright/web/src/modules/utils/urls"
import gql from "graphql-tag"
import { cohortEndsWhenLicenseExpires } from "group/components/manage/GroupUtils"
import get from "lodash/get"
import moment from "moment"
import { LEARN } from "path/constants"
import styled from "styled-components"
import "./ResourceHeaderDash.css"

const StyledCollectionCohortLink = styled.div`
  margin-top: 10px;
  margin-bottom: 20px;
  text-align: center;
`

const COHORT_QUERY = gql`
  query GroupQuery($id: Int!) {
    cohort: group(id: $id) {
      ...Group
      resource {
        ...Resource
      }
      registration {
        id
        role
        send_due_notifications
        send_discussion_notifications
        path {
          id
        }
      }
      user_source_registration {
        id
        role
      }
      path(type: source) {
        id
      }
    }
  }
  ${GroupFragmentDoc}
  ${ResourceFragmentDoc}
`

const CollectionCohortLink = ({ collectionCohortId, section, mode }) => {
  const { data } = useQuery(COHORT_QUERY, {
    variables: {
      id: collectionCohortId
    },
    skip: !collectionCohortId
  })

  const collectionCohort = get(data, "cohort")

  if (!collectionCohort) return null

  // user's path id for registration in parent cohort
  const pathId = get(collectionCohort, "registration.path.id")

  const toGroup = {
    pathname: getGroupUrl(
      collectionCohort.resource.slug,
      collectionCohort.id,
      section
    )
  }

  // navigate to the learner path if currently in learn mode
  if (pathId && mode === LEARN) {
    toGroup.query = {
      path_id: pathId
    }
  }

  return (
    <StyledCollectionCohortLink>
      <Button styleType="text" to={toGroup} inverted>
        {collectionCohort.resource.name}
      </Button>
    </StyledCollectionCohortLink>
  )
}

CollectionCohortLink.displayName = "CollectionCohortLink"

const ResourceHeaderDash = ({
  resource,
  offering,
  mode,
  permissions,
  toPerformance,
  downloadCertificateUrl,
  progress,
  section,
  communityArea,
  t,
  tc
}) => (
  <div className="ResourceHeaderDash">
    <CollectionCohortLink
      collectionCohortId={offering.parent_offering_id}
      section={section}
      mode={mode}
    />
    <div className="ResourceHeaderDash__wrapper">
      <Text.H1
        inverted
        className="ResourceHeaderDash__name theme-heading theme-heading-bold"
      >
        {resource.name}
      </Text.H1>
    </div>
    <div className="ResourceHeaderDash__switcher-wrapper">
      <CohortIsland cohortId={offering.id} section={section} />
    </div>
    {progress === 1 && mode <= LEARN && !!downloadCertificateUrl && (
      <div className="ResourceHeaderDash__achievement">
        <Button inverted to={downloadCertificateUrl} download styleType="text">
          <Pathicon icon="download" />
          <span> </span>
          {t("resource.share_achievement")}
        </Button>
      </div>
    )}
    {!!offering.access_ends && (
      <div style={{ textAlign: "center" }}>
        <Tooltip title={moment(offering.access_ends).format("MMM Do, h:mma")}>
          <span className="ResourceHeaderDash__access_ends">
            <Pathicon icon="calendar" />
            <span> </span>
            {moment(offering.access_ends).isAfter()
              ? cohortEndsWhenLicenseExpires(offering)
                ? tc("Expires {{ fromNowDate }}", {
                    fromNowDate: moment(offering.access_ends).fromNow()
                  })
                : tc("Ends {{ fromNowDate }}", {
                    fromNowDate: moment(offering.access_ends).fromNow()
                  })
              : cohortEndsWhenLicenseExpires(offering)
              ? tc("Expired {{ fromNowDate }}", {
                  fromNowDate: moment(offering.access_ends).fromNow()
                })
              : tc("Ended {{ fromNowDate }}", {
                  fromNowDate: moment(offering.access_ends).fromNow()
                })}
          </span>
        </Tooltip>
      </div>
    )}
  </div>
)

ResourceHeaderDash.displayName = "ResourceHeaderDash"

export default withTranslate(ResourceHeaderDash)
